import type { QueryClient } from '@tanstack/react-query';
import type { Store } from '@mews-commander/types';
import { AppError, CaptchaService, FeatureFlagService, GainsightService, PciProxyService } from '@mews/framework';
import type { ResolveRoute } from '@mews/routing';
import type { Nullable } from '@mews/std';

interface AppInstance {
    isRunning: boolean;
    container: CommanderContainer;
    store: Store;
    resolveRoute: ResolveRoute;
    initChain: Promise<void>;
    featureFlagService: FeatureFlagService;

    AppClockServiceProvider: React.ComponentType<React.PropsWithChildren<{}>>;
    CommanderContextProvider: React.ComponentType<React.PropsWithChildren<{}>>;
    IntlLocalizationProvider: React.ComponentType<React.PropsWithChildren<{}>>;
    LocalizationProvider: React.ComponentType<React.PropsWithChildren<{ localizationHandlers: unknown }>>;
    localizationHandlers: unknown;
    FeatureFlagServiceProvider: React.ComponentType<React.PropsWithChildren<{}>>;
    LoggerServiceProvider: React.ComponentType<React.PropsWithChildren<{}>>;
    RouteResolveProvider: React.ComponentType<React.PropsWithChildren<{}>>;
    RepositoryRegistryProvider: React.ComponentType<React.PropsWithChildren<{}>>;
    reactQueryClient: QueryClient;
    gainsightService: GainsightService;
    pciProxyService: PciProxyService;
    GraphqlClientConfigProvider: React.ComponentType<React.PropsWithChildren<{}>>;
    captchaService: CaptchaService;
}

let appInstance: Nullable<AppInstance> = {
    isRunning: false,
    container: null,
    store: null,
    resolveRoute: null,
    featureFlagService: null,

    initChain: null,

    AppClockServiceProvider: null,
    CommanderContextProvider: null,
    IntlLocalizationProvider: null,
    LocalizationProvider: null,
    localizationHandlers: null,
    FeatureFlagServiceProvider: null,
    LoggerServiceProvider: null,
    RouteResolveProvider: null,
    RepositoryRegistryProvider: null,
    reactQueryClient: null,
    gainsightService: null,
    pciProxyService: null,
    GraphqlClientConfigProvider: null,
    captchaService: null,
};
export const setAppInstance = (app: Omit<AppInstance, 'initChain'>) => {
    appInstance = app as AppInstance;
};
export const setAppInitChain = (initChain: AppInstance['initChain']) => {
    appInstance.initChain = initChain;
};
export const container = withRunCheck(() => appInstance.container);

let testContainerGetter: () => CommanderContainer;
/**
 * @deprecated Use only in tests, and ideally don't use at all
 */
export const setTestContainerGetter = (newTestContainerGetter: () => CommanderContainer) => {
    testContainerGetter = newTestContainerGetter;
};
/**
 * @deprecated
 */
export const getContainer = (): (() => CommanderContainer) => {
    if (process.env.NODE_ENV === 'test' && testContainerGetter) {
        return testContainerGetter;
    }

    return container;
};
/** @deprecated Use only as last resolve */
export const store = withRunCheck(() => appInstance.store);
/** @deprecated Use resolveRoute from thunk or useResolveRoute hook */
export const getResolveRoute = withRunCheck(() => appInstance.resolveRoute);
/** @deprecated Use only as last resolve in complex selectors */
export const getFeatureFlagService = () =>
    process.env.NODE_ENV === 'test'
        ? ({ hasFlag: () => false } as unknown as FeatureFlagService)
        : withRunCheck(() => appInstance.featureFlagService)();
export const getInitChain = withRunCheck(async () => appInstance.initChain);
export const appClockServiceProvider = withRunCheck(() => appInstance.AppClockServiceProvider);
export const commanderContextProvider = withRunCheck(() => appInstance.CommanderContextProvider);
export const intlLocalizationProvider = withRunCheck(() => appInstance.IntlLocalizationProvider);
export const localizationProvider = withRunCheck(() => appInstance.LocalizationProvider);
export const localizationHandlers = withRunCheck(() => appInstance.localizationHandlers);
export const featureFlagServiceProvider = withRunCheck(() => appInstance.FeatureFlagServiceProvider);
export const loggerServiceProvider = withRunCheck(() => appInstance.LoggerServiceProvider);
export const routeResolveProvider = withRunCheck(() => appInstance.RouteResolveProvider);
export const repositoryRegistryProvider = withRunCheck(() => appInstance.RepositoryRegistryProvider);
export const graphqlClientConfigProvider = withRunCheck(() => appInstance.GraphqlClientConfigProvider);
export const getReactQueryClient = withRunCheck(() => appInstance.reactQueryClient);
export const getGainsightService = withRunCheck(() => appInstance.gainsightService);
export const getPciProxyService = withRunCheck(() => appInstance.pciProxyService);
export const getCaptchaService = withRunCheck(() => appInstance.captchaService);

export function checkIsRunning() {
    if (!appInstance.isRunning) {
        throw new AppError('Commander application has not been started yet.');
    }
}

function withRunCheck<T>(fn: () => T) {
    return () => {
        checkIsRunning();
        return fn() as NonNullable<T>;
    };
}
